import { useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { useBranding, useDocumentTitle } from '../lib/branding';
import { Logo } from '../components/Logo';
import { Notice } from '../components/Status';

export function LoginPage() {
  const { login, completeSetup, needsSetup } = useAuth();
  const brand = useBranding();
  useDocumentTitle(needsSetup ? 'Set up' : 'Sign in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (needsSetup) await completeSetup(token, email, password);
      else await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card card">
        <div className="login-brand">
          <Logo size={44} />
          <div>
            <h1>{brand.productName}</h1>
            <p className="muted">{brand.tagline}</p>
          </div>
        </div>
        <form onSubmit={submit} className="login-form">
          {needsSetup && (
            <>
              <Notice level="info">
                Create the first administrator account. The setup code is printed in the server log when the monitor starts.
              </Notice>
              <div className="field">
                <label htmlFor="token">Setup code</label>
                <input id="token" className="input mono" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" required />
              </div>
            </>
          )}
          <div className="field">
            <label htmlFor="email">Email address</label>
            <input id="email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required autoFocus />
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={needsSetup ? 'new-password' : 'current-password'}
              minLength={needsSetup ? 10 : undefined}
              required
            />
            {needsSetup && <span className="hint">At least 10 characters.</span>}
          </div>
          {error && <Notice level="fail">{error}</Notice>}
          <button type="submit" className="btn btn-primary login-submit" disabled={busy}>
            {needsSetup ? 'Create administrator' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
